// Lokaliserad blogg-API: serverar SV som vanligt, lazy-AI-översätter EN
// vid första besöket och cachar resultatet i `blog_posts.{...}_en` kolumnerna.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { getRequest } from "@tanstack/react-start/server";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { translateFields } from "@/lib/translate.server";
import { checkRateLimit, getClientIp } from "@/lib/rate-limit.server";

// Cooldown after a failed translation before we retry the gateway.
const FAILED_RETRY_COOLDOWN_MS = 6 * 60 * 60 * 1000; // 6h
const failureCache = new Map<string, number>();
function markFailure(slug: string) {
  failureCache.set(slug, Date.now());
}
function isInFailureCooldown(slug: string): boolean {
  const t = failureCache.get(slug);
  if (!t) return true; // ingen lokal info — lita på DB-status och vänta ut cooldown
  return Date.now() - t < FAILED_RETRY_COOLDOWN_MS;
}

const Locale = z.enum(["sv", "en"]);

export interface LocalizedPostSummary {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  read_minutes: number;
  cover_emoji: string;
  published_at: string;
}

export interface LocalizedPostFull extends LocalizedPostSummary {
  body: string;
  author_name: string;
  author_role: string;
  seo_title: string | null;
  seo_description: string | null;
  updated_at: string | null;
}

const LIST_FIELDS_SV =
  "slug, title, excerpt, category, read_minutes, cover_emoji, published_at";
const LIST_FIELDS_EN =
  "slug, title, title_en, excerpt, excerpt_en, category, read_minutes, cover_emoji, published_at, translation_status_en";

export const listBlogPostsLocalized = createServerFn({ method: "GET" })
  .inputValidator((input) => z.object({ locale: Locale }).parse(input))
  .handler(async ({ data }) => {
    const fields = data.locale === "en" ? LIST_FIELDS_EN : LIST_FIELDS_SV;
    const { data: posts, error } = await supabaseAdmin
      .from("blog_posts")
      .select(fields)
      .eq("status", "published")
      .order("published_at", { ascending: false });
    if (error) throw new Error(error.message);

    const rows = (posts ?? []) as unknown as Array<Record<string, unknown>>;
    const list = rows.map((p) => {
      const title = data.locale === "en" && p.title_en ? String(p.title_en) : String(p.title);
      const excerpt =
        data.locale === "en" && p.excerpt_en ? String(p.excerpt_en) : String(p.excerpt);
      return {
        slug: String(p.slug),
        title,
        excerpt,
        category: String(p.category),
        read_minutes: Number(p.read_minutes),
        cover_emoji: String(p.cover_emoji),
        published_at: String(p.published_at),
      } satisfies LocalizedPostSummary;
    });
    return { posts: list };
  });

export const getBlogPostLocalized = createServerFn({ method: "GET" })
  .inputValidator((input) =>
    z.object({ slug: z.string().min(1).max(200), locale: Locale }).parse(input),
  )
  .handler(async ({ data }) => {
    const { data: post, error } = await supabaseAdmin
      .from("blog_posts")
      .select(
        "slug, title, excerpt, body, category, read_minutes, cover_emoji, published_at, updated_at, author_name, author_role, seo_title, seo_description, title_en, excerpt_en, body_en, seo_title_en, seo_description_en, translation_status_en",
      )
      .eq("status", "published")
      .eq("slug", data.slug)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!post)
      return {
        post: null as LocalizedPostFull | null,
        related: [] as LocalizedPostSummary[],
      };

    let title = post.title as string;
    let excerpt = post.excerpt as string;
    let body = post.body as string;
    let seo_title = post.seo_title as string | null;
    let seo_description = post.seo_description as string | null;

    if (data.locale === "en") {
      const cached = post.translation_status_en === "ok";
      if (cached && post.title_en && post.body_en) {
        title = String(post.title_en);
        excerpt = String(post.excerpt_en ?? post.excerpt);
        body = String(post.body_en);
        seo_title = post.seo_title_en ? String(post.seo_title_en) : null;
        seo_description = post.seo_description_en ? String(post.seo_description_en) : null;
      } else if (
        post.translation_status_en === "translating" ||
        (post.translation_status_en === "failed" &&
          isInFailureCooldown(data.slug))
      ) {
        // En översättning pågår redan, eller har nyligen misslyckats —
        // fall tillbaka till SV utan att trigga gateway:n igen.
      } else {
        // Per-IP rate limit för att skydda AI-gateway-kvoten mot missbruk.
        let ip: string | null = null;
        try {
          ip = getClientIp(getRequest());
        } catch {
          ip = null;
        }
        const allowed = checkRateLimit({
          scope: "blog-translate",
          keys: [ip ?? "anon", data.slug],
          limit: 5,
          windowMs: 60 * 60 * 1000,
        });
        if (!allowed) {
          // Returnera SV-fallback istället för att översätta.
        } else {
          // Lås raden så samtidiga requests ser "translating" och faller
          // tillbaka till SV istället för att trigga parallella gateway-anrop.
          const { data: locked } = await supabaseAdmin
            .from("blog_posts")
            .update({ translation_status_en: "translating" })
            .eq("slug", data.slug)
            .neq("translation_status_en", "translating")
            .neq("translation_status_en", "ok")
            .select("slug")
            .maybeSingle();

          if (locked) {
            try {
              const translated = await translateFields(
                {
                  title: post.title as string,
                  excerpt: post.excerpt as string,
                  body: post.body as string,
                  seo_title: (post.seo_title as string | null) ?? "",
                  seo_description: (post.seo_description as string | null) ?? "",
                },
                { body: "md" },
              );
              title = translated.title || title;
              excerpt = translated.excerpt || excerpt;
              body = translated.body || body;
              seo_title = translated.seo_title || null;
              seo_description = translated.seo_description || null;

              await supabaseAdmin
                .from("blog_posts")
                .update({
                  title_en: title,
                  excerpt_en: excerpt,
                  body_en: body,
                  seo_title_en: seo_title,
                  seo_description_en: seo_description,
                  translation_status_en: "ok",
                })
                .eq("slug", data.slug);
            } catch (err) {
              console.error("[blog-translate] failed for", data.slug, err);
              markFailure(data.slug);
              await supabaseAdmin
                .from("blog_posts")
                .update({ translation_status_en: "failed" })
                .eq("slug", data.slug);
            }
          }
        }
      }
    }


    const fullPost: LocalizedPostFull = {
      slug: post.slug as string,
      title,
      excerpt,
      body,
      category: post.category as string,
      read_minutes: Number(post.read_minutes),
      cover_emoji: post.cover_emoji as string,
      published_at: post.published_at as string,
      author_name: post.author_name as string,
      author_role: post.author_role as string,
      seo_title,
      seo_description,
      updated_at: (post.updated_at as string | null) ?? null,
    };

    // Related posts: alltid 3 nyaste, samma lokal-fallback.
    const relatedFields = data.locale === "en" ? LIST_FIELDS_EN : LIST_FIELDS_SV;
    const { data: related } = await supabaseAdmin
      .from("blog_posts")
      .select(relatedFields)
      .eq("status", "published")
      .neq("slug", data.slug)
      .order("published_at", { ascending: false })
      .limit(3);

    const relatedRows = (related ?? []) as unknown as Array<Record<string, unknown>>;
    const relatedList = relatedRows.map((p) => ({
      slug: String(p.slug),
      title:
        data.locale === "en" && p.title_en ? String(p.title_en) : String(p.title),
      excerpt:
        data.locale === "en" && p.excerpt_en
          ? String(p.excerpt_en)
          : String(p.excerpt),
      category: String(p.category),
      read_minutes: Number(p.read_minutes),
      cover_emoji: String(p.cover_emoji),
      published_at: String(p.published_at),
    })) satisfies LocalizedPostSummary[];

    return { post: fullPost, related: relatedList };
  });
